import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { axiosInstance } from "../../../axios/axiosInstance";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select2Component from "./select";
import { useAuth } from "../../../Context/AuthContext";
import { isValidArray } from "../../../Layout/Common/common";
import { Tooltip } from "bootstrap";
import { useFlashScreen } from "../../../Context/flashScreen";
import { sameLayerState } from "../productPageOverlay/productPageOverlayContext";
import { useProductOverlay } from "../productOverlay/productOverlayContext";

const optionCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const sortAttrOptions = (list) =>
  list.map((attr) =>
    isValidArray(attr.attrData)
      ? {
          ...attr,
          attrData: [...attr.attrData].sort((a, b) =>
            optionCollator.compare(String(a.filterString ?? ""), String(b.filterString ?? "")),
          ),
        }
      : attr,
  );

export const ProductHybridFilter = ({ params, pageData }) => {
  const [attrList, setAttrList] = useState(null);
  const [colorFilter, setColorFilter] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);
  const [fliterAttrList, setFliterAttrList] = useState(null);
  const [selectedObj, setSelectedObj] = useState(null);
  const colorListRef = useRef(null);
  const { pid, fid } = useParams();
  const { userId, companyId } = useAuth();
  const history = useNavigate();
  const location = useLocation();
  const productOverlay = useProductOverlay();
  const { showFlashScreen } = useFlashScreen();

  useEffect(() => {
    if (params) {
      if (params.selectedValue) {
        setSelectedObj(params.selectedValue);
        setSelectedValue(params.selectedValue);
      }
      if (params.filterRawData) setFliterAttrList(params.filterRawData);
      if (params.colorFilter) setColorFilter(params.colorFilter);
      if (isValidArray(params.attrFilter)) setAttrList(sortAttrOptions(params.attrFilter));
    }
  }, [params]);
  useEffect(() => {
    const buildTooltips = (root, options) => {
      if (!root) return [];
      const instances = [];
      root.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
        const existing = Tooltip.getInstance(el);
        if (existing) existing.dispose();
        instances.push(new Tooltip(el, options));
      });
      return instances;
    };
    const instances = buildTooltips(colorListRef.current);
    return () => {
      instances.forEach((instance) => {
        try {
          instance.dispose();
        } catch (e) {
          console.error(e);
        }
      });
    };
  }, [colorFilter, attrList, selectedObj]);

  const skuMap = useMemo(() => {
    const map = {};
    if (!isValidArray(fliterAttrList)) return map;
    fliterAttrList.forEach((attr) => {
      const values = {};
      if (isValidArray(attr.attrData)) {
        attr.attrData.forEach((item) => {
          const key = String(item.filterString);
          if (!values[key]) values[key] = new Set();
          if (item.sku) values[key].add(item.sku);
        });
      }
      map[attr.attrName] = values;
    });
    return map;
  }, [fliterAttrList]);

  const isOptionAvailable = (attrName, value) => {
    const attrValues = skuMap[attrName];
    if (!attrValues) return true;
    const baseSkus = attrValues[String(value)];
    if (!baseSkus || baseSkus.size === 0) return true;
    let matched = Array.from(baseSkus);
    const entries = selectedObj ? Object.entries(selectedObj) : [];
    for (let i = 0; i < entries.length; i++) {
      const [key, selected] = entries[i];
      if (key === attrName) continue;
      const otherValues = skuMap[key];
      if (!otherValues) continue;
      const otherSkus = otherValues[String(selected)];
      if (!otherSkus || otherSkus.size === 0) continue;
      matched = matched.filter((sku) => otherSkus.has(sku));
      if (matched.length === 0) return false;
    }
    return matched.length > 0;
  };

  const otherAttrNames = (attrName) =>
    Object.keys(selectedObj || {})
      .filter((key) => key !== attrName)
      .join(" / ");

  const getConflictText = (attrName, value) => {
    if (isOptionAvailable(attrName, value)) return "";
    const names = otherAttrNames(attrName);
    return names
      ? `${value} comes with a different ${names} - select to see options`
      : `${value} - select to see options`;
  };

  const convertToString = (obj) => {
    return Object.entries(obj)
      .map(([key, value]) => `${key}=${value}`) // Convert key-value pairs to "key=value"
      .join("////"); // Join with "////"
  };

  const filterDataWithKey = (fkey, fvalue) => {
    const attrValues = skuMap[fkey];
    if (!attrValues) return [];
    const skus = attrValues[String(fvalue)];
    return skus ? Array.from(skus) : [];
  };

  const resolveSku = (selectedKey, selectedValue) => {
    let matchedSKUs = filterDataWithKey(selectedKey, selectedValue);
    if (!isValidArray(matchedSKUs)) return null;
    Object.entries(selectedObj || {}).forEach(([key, value]) => {
      if (key === selectedKey || matchedSKUs.length <= 1) return;
      const matchedItems = filterDataWithKey(key, value);
      if (!isValidArray(matchedItems)) return;
      const filterSkuForAttr = matchedSKUs.filter((sku) => matchedItems.includes(sku));
      if (isValidArray(filterSkuForAttr)) matchedSKUs = filterSkuForAttr;
    });
    return matchedSKUs[0];
  };

  const getAttributesBySku = (sku) => {
    const result = {};
    if (!isValidArray(fliterAttrList)) return result;
    fliterAttrList.forEach((attr) => {
      if (!isValidArray(attr.attrData)) return;
      const match = attr.attrData.find((item) => item.sku == sku);
      if (match) result[attr.attrName] = match.filterString;
    });
    return result;
  };

  const handleAttributeChange = (key, value) => {
    const sku = resolveSku(key, value);
    if (!sku) return;
    const resolvedObj = { ...selectedObj, ...getAttributesBySku(sku), [key]: value };
    setSelectedObj(resolvedObj);
    setSelectedValue(resolvedObj);
    if (productOverlay?.changeSku) {
      productOverlay.changeSku(sku);
      return;
    }
    const url = convertToString(resolvedObj).replaceAll("////", "&");
    history("/product/" + fid + "?sku=" + sku + "&" + url, { replace: true, state: sameLayerState(location) });
  };

  return (
    <div className="product-price-length mt-0">
      <div className="proSelect-color mt-0 border-top-style" style={{ borderTop: 0 }}>
        <h5>Buying Options:</h5>
        {colorFilter && (
          <Fragment>
            <label>Select Colour</label>
            <div className="proColors-list">
              <div className="selected-color">
                Colour: <p>{selectedValue?.Colour}</p>
              </div>
              <ul className="pro-list-colors" ref={colorListRef}>
                {colorFilter.attrData.map((item, index) => {
                  const conflict = getConflictText(colorFilter.attrName, item.filterString);
                  const isActive = selectedObj?.[colorFilter.attrName]
                    ? String(selectedObj[colorFilter.attrName]) === String(item.filterString)
                    : item.selected == 1;
                  return (
                    <li
                      key={index}
                      id={`colorselection${index}`}
                      className={`${isActive ? "active" : ""}${conflict ? " unavailable" : ""}`.trim()}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      aria-label={item.filterString}
                      data-bs-original-title={conflict || item.filterString}
                      onClick={() => handleAttributeChange(colorFilter.attrName, item.filterString)}
                    >
                      <span className="productColor" style={{ backgroundColor: `${item.filterString}` }} />
                    </li>
                  );
                })}
              </ul>
            </div>
          </Fragment>
        )}
      </div>
      {isValidArray(attrList) && (
        <div className="length-type">
          {attrList.map((item) => (
            <Select2Component
              key={item.attrName}
              attr={item}
              selectedValue={(selectedObj || {})[item.attrName]}
              onChangeEvent={handleAttributeChange}
              getConflictText={getConflictText}
            />
          ))}
        </div>
      )}
    </div>
  );
};
